import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BlurTextInImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/blur-text-in-image',
			name: 'Blur Text in Image Online — AI-Powered Tool | Lift',
			description:
				"Blur text in any image online with Lift's AI editor. Hide sensitive writing, protect personal details, and clean up photos before sharing. Try it.",
			url: 'https://liftapp.ai/blur-text-in-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/blur-text-in-image#product' },
				{ '@id': 'https://liftapp.ai/blur-text-in-image#howto' },
				{ '@id': 'https://liftapp.ai/blur-text-in-image#benefits' },
				{ '@id': 'https://liftapp.ai/blur-text-in-image#faq' },
				{ '@id': 'https://liftapp.ai/blur-text-in-image#why-lift' },
				{ '@id': 'https://liftapp.ai/blur-text-in-image#explore-tools' },
				{ '@id': 'https://liftapp.ai/blur-text-in-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/blur-text-in-image#product',
			name: 'Blur Text in Image',
			description:
				"Blur text in any image online with Lift's AI editor. Hide sensitive writing, protect personal details, and clean up photos before sharing.",
			url: 'https://liftapp.ai/blur-text-in-image',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/blur-text-in-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your image',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select the text to blur',
					text: 'Use the manual brush or area selector to highlight any text or writing you want to obscure',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Adjust the blur intensity',
					text: 'Set how strong the blur appears — from a light haze to a fully unreadable mask',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-text-in-image#benefits',
			name: 'Who can benefit from Blur Text in Image Tool?',
			description:
				"Lift's blur text tool is ideal for content creators, marketers, small business owners, privacy-conscious users, and anyone who needs to hide text or sensitive writing in photos.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Blur distracting text overlays to direct visual focus where it matters.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Remove visible text from product photos to create cleaner, brand-ready visuals.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Blur confidential text in screenshots or documents before sharing externally.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Privacy-Conscious Users — Hide addresses, names, or sensitive writing in photos before posting online.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Casual Users — Blur words in any photo directly in the browser, no software or skills required.',
				},
			],
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/blur-text-in-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Blur Text in Image Tool?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Lift's Blur Text in Image Tool lets you selectively blur any text, words, or writing visible in a photo. It runs entirely in your browser — no installation needed. Use it to hide sensitive information, blur image text before sharing, or clean up screenshots for professional use.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Blur text in images tool work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your photo and use the brush or selection tool to mark the text you want to obscure. Adjust the intensity slider to control how unreadable the text becomes, then download the finished image. The entire process takes under a minute.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Blur Text in Image Tool uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-text-in-image#why-lift',
			name: "Why Lift's blur text tool stands out",
			description:
				"Lift's blur text tool combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-text-in-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/blur-text-in-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Blur Text in Image',
					item: 'https://liftapp.ai/blur-text-in-image',
				},
			],
		},
	],
};
